/** @jsxImportSource @emotion/react */
import { jsx, css } from "@emotion/react";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "@table-library/react-table-library/theme";
import {
  DEFAULT_OPTIONS,
  getTheme,
} from "@table-library/react-table-library/material-ui";
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table";
import { useTranslation } from "react-i18next";
import { GroupInfo, GroupMember } from "../types/gigya";
import { GigyaGroupModels } from "../types/gigya.schema";
import { useEffect, useState } from "react";
import { Button } from "@mui/material";

type FormattedGroupMemberInfo = {
  label: string;
  value: string;
  button: string;
  buttonAction?: () => void;
};

type SEARCHGROUP = {
  UID: string;
  groupId: string;
  model: string;
  relationshipData: {
    role: string;
    status: string;
  };
  profile: {
    lastName: string;
    country: string;
    locale: string;
    email: string;
    zip: string;
    firstName: string;
  };
  memberSince: string;
  lastUpdated: string;
  memberSinceTimestamp: number;
  lastUpdatedTimestamp: number;
  permissions: string;
};

type SEARCHGROUPINFO = {
  groupId: string;
  model: string;
  groupData: {
    clinicName: string;
    priceGroup: string;
    country: string;
    city: string;
    SFHomeDeliveryOnly: boolean;
    industry: string;
    salesOrg: string;
    altID: string;
    payer: string;
    archived: boolean;
    salesZone: string;
    clinicType: string;
    customerClass: string;
    billTo: string;
    softClosed: boolean;
    state: string;
    billingZip: string;
    zip: string;
    companyCode: string;
    soldToClinicName: string;
    soldTo: string;
    customerGroup: string;
    customerPlanGroup: string;
    salesTerritory: string;
    accountType: string;
    approvedEmails: string;
    streetAddress: string;
    blockedClinic: boolean;
    paymentTerm: string;
    billingCity: string;
    shipTo: string;
    salesRegion: string;
    customerPriceProcedure: string;
  };
  created: string;
  lastUpdated: string;
  createdTimestamp: number;
  lastUpdatedTimestamp: number;
  membersCount: number;
};

type TableMemberProps = {
  myUID: string;
  groupMemberInfo: GroupMember;
  memberGroups: GroupInfo<GigyaGroupModels>[];
  searchClinicsInfo: SEARCHGROUPINFO | undefined;
  searchClinics: SEARCHGROUP[];
  handleEditClick: () => void;
  selectedGroupIndex: number;
  nodes?: FormattedGroupMemberInfo[];
};

// IMPLEMENT CONTEXT FOR CURRENT USER, MEMBERS, API METHODS
// IMPLEMENT REACT API
export const TableMember = (props: TableMemberProps) => {
  const {
    groupMemberInfo,
    memberGroups,
    searchClinicsInfo,
    searchClinics,
    handleEditClick,
    selectedGroupIndex,
    myUID,
  } = props;
  const { t } = useTranslation();

  const formatGroupMemberInfo = () => {
    if (
      process.env.REACT_APP_LOCALE !== "en-us" &&
      searchClinicsInfo &&
      groupMemberInfo &&
      searchClinics
    ) {
      return [
        {
          label: "Name",
          value: `${groupMemberInfo.profile?.lastName}, ${groupMemberInfo.profile?.firstName}`,
          button: "",
        },
        {
          label: "Team Official Name",
          value: searchClinicsInfo.groupData.clinicName,
          button: "",
        },
        {
          label: "Team Nickname",
          value: groupMemberInfo.relationshipData.nickname,
          button: "edit",
          buttonAction: handleEditClick,
        },
        {
          label: "Role",
          value:
            searchClinics[
              selectedGroupIndex
            ]?.relationshipData.role?.toString() || "",
          button: "",
        },
        {
          label: "Status",
          value:
            searchClinics[
              selectedGroupIndex
            ]?.relationshipData.status?.toString() || "",
          button: "",
        },
        {
          label: "Hill’s “Ship To” Account Number",
          value: searchClinicsInfo.groupId,
          button: "",
        },
        {
          label: "Informational Details",
          value: "View or update your Team’s details here.",
          button: "",
        },
      ];
    }

    if (process.env.REACT_APP_LOCALE === "en-us" && groupMemberInfo) {
      const group =
        memberGroups[
          process.env.REACT_APP_LOCALE !== "en-us" ? 0 : selectedGroupIndex
        ];

      return [
        {
          label: "Name",
          value: `${groupMemberInfo.profile?.lastName}, ${groupMemberInfo.profile?.firstName}`,
          button: "",
        },
        {
          label: "Team Official Name",
          value: group.groupData.clinicName.toString(),
          button: "",
        },
        {
          label: "Team Nickname",
          value: groupMemberInfo.relationshipData.nickname?.toString() || "",
          button: "edit",
          buttonAction: handleEditClick,
        },
        {
          label: "Role",
          value: group.relationshipData.role?.toString() || "",
          button: "",
        },
        {
          label: "Status",
          value: group.relationshipData.status?.toString() || "",
          button: "",
        },
        {
          label: "Hill’s “Ship To” Account Number",
          value: group.groupId,
          button: "",
        },
        {
          label: "Informational Details",
          value: "View or update your Team’s details here.",
          button: "",
        },
      ];
    }

    return [];
  };

  const [nodes, setNodes] = useState<FormattedGroupMemberInfo[]>([]);

  const labelStyle = css`
    font-family: "Montserrat";
    font-size: 20px;
    font-style: "normal";
    font-weight: 700;
    line-height: 26px;
  `;

  const valueStyle = css`
    font-family: "Montserrat";
    font-size: 18px;
    font-style: "normal";
    font-weight: 400;
    line-height: 31px;
  `;

  const editStyle = css`
    display: flex;
    justify-content: space-between;
  `;

  const customTheme = {
    HeaderRow: `
      &.tr-header th {
        background-color: unset;
        font-weight: normal;
        text-align: unset;
        border: none;
        padding: 0;
      };
    `,
    BaseRow: ``,
    Row: `
      &.td {
        border: none;
        padding: 0;
      };

      &.tr-body.team-management td {
        border-top: none;
        border-left: none;
        border-right: none;
        border-bottom: 1px solid #e0e0e0;
        padding: 0;
      };
    `,
    BaseCell: `
      padding: 0;
      margin-bottom: 15px;
    `,
    Cell: `
      border: none;
      padding: 0;
    `,
  };

  const theme = useTheme([getTheme(DEFAULT_OPTIONS), customTheme]);

  useEffect(() => {
    setNodes(formatGroupMemberInfo());
  }, [searchClinics, searchClinicsInfo, groupMemberInfo]);

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const renderValue = (label: string, value: string) => {
    if (label === "Role") {
      return <p>{t(`exported.${capitalizeFirstLetter(value)}`)}</p>;
    }

    if (label === "Status" && value === "approved") {
      return <p>{t("approve")}</p>;
    }

    if (label === "Status" && value === "rejected") {
      return <p>{t("reject")}</p>;
    }

    if (label === "Status" && value === "pending") {
      return <p>{t("pendingRequest")}</p>;
    }

    if (
      label === "Informational Details" &&
      value === "View or update your Team’s details here."
    ) {
      return <p>{t("exported.View or update your Team’s details here.")}</p>;
    }

    return <p>{value}</p>;
  };

  const memberData = { nodes };

  return (
    <>
      <Table data={memberData} theme={theme}>
        {(tableList: any[]) => (
          <>
            <Header>
              <HeaderRow key="table-member-hr">
                <HeaderCell></HeaderCell>
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map((item) => (
                <Row className="team-management" key={item.id} item={item}>
                  {!item.button &&
                    item.label !== "Status" &&
                    process.env.REACT_APP_LOCALE === "en-us" && (
                      <Cell>
                        <div css={labelStyle}>
                          {t(`exported.${capitalizeFirstLetter(item.label)}`)}
                        </div>
                        <div css={valueStyle}>
                          {renderValue(item.label, item.value)}
                        </div>
                      </Cell>
                    )}
                  {!item.button && process.env.REACT_APP_LOCALE !== "en-us" && (
                    <Cell>
                      <div css={labelStyle}>
                        {t(`exported.${capitalizeFirstLetter(item.label)}`)}
                      </div>
                      <div css={valueStyle}>
                        {renderValue(item.label, item.value)}
                      </div>
                    </Cell>
                  )}
                  {item.button && (
                    <Cell>
                      <div css={editStyle}>
                        <div>
                          <div css={labelStyle}>
                            {t(`exported.${capitalizeFirstLetter(item.label)}`)}
                          </div>
                          <div css={valueStyle}>{item.value}</div>
                        </div>
                        <IconButton
                          aria-label="edit"
                          onClick={item.buttonAction}
                        >
                          <EditIcon />
                        </IconButton>
                      </div>
                    </Cell>
                  )}
                </Row>
              ))}
            </Body>
          </>
        )}
      </Table>
    </>
  );
};
